
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Heart, 
  MessageCircle, 
  Share, 
  ThumbsUp, 
  Trash2,
  FileText,
  Link as LinkIcon
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Post } from "@/types/post";
import { formatDate, getInitials, truncateText } from "@/lib/utils";
import { usePostsStore } from "@/store/postsStore";
import { useToast } from "@/hooks/use-toast";
import { sanitizeInput } from "@/utils/security";

interface PostCardProps {
  post: Post;
  isDetailed?: boolean;
  onViewDetails?: (postId: string) => void;
}

export const PostCard: React.FC<PostCardProps> = ({ 
  post, 
  isDetailed = false,
  onViewDetails 
}) => {
  const { likePost, addComment, deletePost } = usePostsStore();
  const { toast } = useToast();
  const [commentText, setCommentText] = useState("");
  const [showComments, setShowComments] = useState(false);

  const handleLike = () => {
    likePost(post.id);
    toast({
      title: "Post liked!",
      description: "You've liked this post.",
    });
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.origin + '/posts/' + post.id);
    toast({
      title: "Link copied!",
      description: "Post link copied to clipboard",
    });
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    addComment(post.id, {
      content: commentText,
      authorId: "user123", // In a real app, this would come from auth
      authorName: "Current User", // In a real app, this would come from auth
    });

    setCommentText("");
    toast({
      title: "Comment added",
      description: "Your comment has been added to the post",
    });
  };

  const handleDelete = () => {
    deletePost(post.id);
    toast({
      title: "Post deleted",
      description: "The post has been successfully deleted",
    });
  };
  
  return (
    <Card className="border-blue-100 overflow-hidden transition-all duration-300 hover:shadow-md">
      <CardHeader className="bg-gradient-to-r from-sky-50 to-emerald-50 p-4 flex flex-row items-center gap-3">
        <Avatar>
          <AvatarImage src={post.authorAvatar} />
          <AvatarFallback className="bg-sky-200 text-sky-700">
            {getInitials(post.authorName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="font-semibold text-gray-900">{post.authorName}</div>
          <div className="text-xs text-gray-500">{formatDate(post.createdAt)}</div>
        </div>
        {/* Only show delete button if the current user is the author (in a real app, check userId) */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-gray-500 hover:text-red-500"
          onClick={handleDelete}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="p-4">
        <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
        <p className="text-gray-700 mb-4">
          {isDetailed ? post.content : truncateText(post.content, 150)}
          {!isDetailed && post.content.length > 150 && (
            <Button 
              variant="link" 
              className="p-0 h-auto text-sky-600"
              onClick={() => onViewDetails && onViewDetails(post.id)}
            >
              Read more
            </Button>
          )}
        </p>
        
        {post.attachments && post.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.attachments.map(attachment => (
              <div 
                key={attachment.id}
                className="flex items-center p-2 bg-gray-50 rounded-md text-sm"
              >
                <FileText className="h-4 w-4 mr-2 text-sky-500" />
                <span className="text-gray-700">{attachment.name}</span>
              </div>
            ))}
          </div>
        )}
        
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag, index) => (
              <Badge key={index} variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {post.projectId && (
          <div className="flex items-center text-sm text-gray-600 mb-2">
            <LinkIcon className="h-4 w-4 mr-1 text-sky-500" />
            <span>
              Related to project: 
              <Button variant="link" className="p-0 h-auto text-sky-600 ml-1">
                View Project
              </Button>
            </span>
          </div>
        )}
      </CardContent>
      <CardFooter className="p-4 pt-0 flex flex-col gap-4">
        <div className="flex justify-between w-full">
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleLike}>
              <Heart className={`h-4 w-4 mr-1 ${post.likes > 0 ? 'text-red-500 fill-red-500' : 'text-gray-500'}`} />
              <span>{post.likes}</span>
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowComments(!showComments)}
            >
              <MessageCircle className="h-4 w-4 mr-1 text-gray-500" />
              <span>{post.comments.length}</span>
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={handleShare}>
            <Share className="h-4 w-4 mr-1 text-gray-500" />
            <span>Share</span>
          </Button>
        </div>

        {showComments && (
          <div className="w-full space-y-4">
            {post.comments.length > 0 && (
              <div className="space-y-3">
                {post.comments.map(comment => (
                  <div key={comment.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs bg-emerald-100 text-emerald-700">
                        {getInitials(comment.authorName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{comment.authorName}</span>
                        <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                      </div>
                      <p className="text-sm text-gray-700">{comment.content}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0"
                      onClick={() => {
                        // Like comment functionality would go here
                        toast({
                          title: "Coming soon",
                          description: "Comment liking will be available soon",
                        });
                      }}
                    >
                      <ThumbsUp className="h-4 w-4 text-gray-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleAddComment} className="flex gap-2">
              <Input
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(sanitizeInput(e.target.value))}
                className="flex-1"
              />
              <Button 
                type="submit" 
                disabled={!commentText.trim()}
                className="bg-sky-500 hover:bg-sky-600"
              >
                Send
              </Button>
            </form>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};
